import { Box, Typography } from "@mui/material";
import { DetailsTable } from "components/DetailsTable/DetailsTable";
import { SegmentsFetcher } from "components/Layout/Campaign/Common/CampaignBasicSettings/Sections/SegmentsFetcher";
import { TiersFetcher } from "components/Layout/Campaign/Common/CampaignBasicSettings/Sections/TiersFetcher";
import { useMessage } from "hooks/intl";
import { useState } from "react";
import { CampaignBEData, VisibilityTarget } from "types/campaigns";
import { OLDate } from "utilities/dateFormat";
import { useStyles } from "./styles";

interface Props {
  leaderboardData?: CampaignBEData;
}

const LeaderboardDetails = ({ leaderboardData }: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const [listOfFetchedElements, setListOfFetchedElements] = useState<any[]>([]);

  const formatTimeframe = () => {
    const startDate = OLDate(leaderboardData?.activity?.startsAt || "");
    const endDate = OLDate(leaderboardData?.activity?.endsAt || "");

    if (!endDate) return `${message({ id: "from" })} ${startDate}`;

    return `${message({ id: "from" })} ${startDate} ${message({ id: "to" }).toLowerCase()} ${endDate}`;
  };

  const formatVisibility = () => {
    switch (leaderboardData?.visibility?.target) {
      case VisibilityTarget.TIER:
        return (
          <>
            {message({ id: "targetedTiers" })}:{" "}
            {leaderboardData?.visibility.tiers?.map((item: string, index) => (
              <TiersFetcher
                id={item}
                key={index}
                setListOfFetchedTiers={(data) =>
                  setListOfFetchedElements((previousArrayState) => [
                    ...previousArrayState,
                    data,
                  ])
                }
              />
            ))}
            {listOfFetchedElements &&
              listOfFetchedElements.map((tier) => tier.name).join(", ")}
          </>
        );
      case VisibilityTarget.SEGMENT:
        return (
          <>
            {message({
              id: "targetedSegments",
            })}
            :{" "}
            {leaderboardData?.visibility.segments?.map(
              (item: string, index) => (
                <SegmentsFetcher
                  id={item}
                  key={index}
                  setListOfFetchedSegments={(data) => {
                    setListOfFetchedElements((previousArrayState) => [
                      ...previousArrayState,
                      data,
                    ]);
                  }}
                />
              ),
            )}{" "}
            {listOfFetchedElements &&
              listOfFetchedElements.map((segment) => segment.name).join(", ")}
          </>
        );
      case VisibilityTarget.NONE:
        return message({ id: "hidden" });
      default:
        return message({ id: "everyone" });
    }
  };

  const basicSettingsItems = [
    {
      label: message({ id: "name" }),
      value: leaderboardData?.name,
    },
    {
      label: message({ id: "description" }),
      value: leaderboardData?.description,
    },
    {
      label: message({ id: "timeframe" }),
      value: formatTimeframe(),
    },
    {
      label: message({ id: "visibility" }),
      value: formatVisibility(),
    },
  ];

  return (
    <Box className={classes.section}>
      <Typography
        className={classes.sectionTitle}
        data-test-id="leaderboard-details"
      >
        {message({ id: "leaderboardDetails" })}
      </Typography>
      <DetailsTable
        title={message({ id: "basicSettings" })}
        items={basicSettingsItems}
      />
    </Box>
  );
};

export default LeaderboardDetails;
